import type { CrewlinkClient } from '../supabase-client';
import type { ProjectRow } from '../database.types';

/**
 * Writer wrapper for `create_project` (migration 0002).
 *
 * The layering rule (ARCHITECTURE §2, INV-T02): UI → query hooks → writer
 * wrappers → Postgres RPC. UI code never calls `.rpc(...)` directly and never
 * calls `.insert(...)` at all.
 *
 * Only the name crosses this boundary. The owner is read from `auth.uid()`
 * inside the function, so there is no argument through which a caller could
 * claim another owner (INV-T23).
 */
export async function createProject(client: CrewlinkClient, name: string): Promise<ProjectRow> {
  const { data, error } = await client.rpc('create_project', { p_name: name });

  if (error) throw error;
  if (!data) throw new Error('create_project returned no project row');

  return data;
}

/**
 * Turn a failure into something Brandon can act on.
 *
 * The database is the validation authority — `project_name_not_blank` and
 * `project_name_max_length` from migration 0001 — so these messages translate
 * its constraint violations rather than re-implementing the rules in the
 * client, where the two could drift apart.
 */
export function describeCreateProjectError(message: string): string {
  const normalised = message.toLowerCase();

  if (normalised.includes('project_name_not_blank')) {
    return 'Give the Project a name before creating it.';
  }
  if (normalised.includes('project_name_max_length')) {
    return 'That name is too long. Use 200 characters or fewer.';
  }
  if (normalised.includes('not authenticated') || normalised.includes('42501')) {
    return 'Your session has expired. Sign in again and try once more.';
  }
  if (normalised.includes('failed to fetch') || normalised.includes('network')) {
    return 'Could not reach Crewlink. Check your connection and try again.';
  }
  return 'Could not create the Project. Please try again.';
}
